"use client";
import * as React from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { ChevronsUpDown, Loader2, Check, X } from "lucide-react";
import userService, { type UserRole } from "@/services/userService";
import type { User } from "@/types/auth";
import { cn } from "@/lib/utils";

type Props = {
  role?: UserRole;                 // enforce role in results
  value: string | null | undefined; // selected userId
  onChange: (userId: string | null, user?: User) => void;
  placeholder?: string;
  disabled?: boolean;
  pageSize?: number;               // default 20
  className?: string;
};

function userLabel(u: User) {
  const name = [u.firstName ?? "", u.lastName ?? ""].join(" ").trim();
  return name ? `${name} — ${u.email}` : u.email ?? u.id;
}

export const AsyncUserSelect: React.FC<Props> = ({
  role,
  value,
  onChange,
  placeholder = "Search users…",
  disabled,
  pageSize = 20,
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState<User[]>([]);
  const [selected, setSelected] = React.useState<User | null>(null);

  // pagination refs
  const pageRef = React.useRef(1);
  const hasMoreRef = React.useRef(true);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = React.useCallback(
    async (reset = false) => {
      setLoading(true);
      try {
        const page = reset ? 1 : pageRef.current;
        const { users, hasMore, nextPage } = await userService.search({
          q,
          role,
          page,
          pageSize,
        });
        const filtered = role ? users.filter((u) => u.role === role) : users;
        setItems((prev) => (reset ? filtered : [...prev, ...filtered]));
        pageRef.current = nextPage ?? (hasMore ? page + 1 : page);
        hasMoreRef.current = hasMore;
      } finally {
        setLoading(false);
      }
    },
    [q, role, pageSize]
  );

  React.useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(true), 250);
  }, [open, q, runSearch]);

  React.useEffect(() => {
    (async () => {
      if (!value) {
        setSelected(null);
        return;
      }
      const inList = items.find((u) => u.id === value);
      if (inList) {
        setSelected(inList);
        return;
      }
      const [user] = await userService.getByIds([value]);
      setSelected(user ?? null);
    })();
  }, [value]);

  const handleSelect = (u: User) => {
    setSelected(u);
    onChange(u.id, u);
    setOpen(false);
  };

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected(null);
    onChange(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between rounded-xl h-10 px-3",
            className
          )}
        >
          <div className="truncate text-left">
            {selected ? userLabel(selected) : <span className="text-muted-foreground">{placeholder}</span>}
          </div>
          <div className="flex items-center gap-2 pl-3">
            {selected && (
    <span
    role="button"
    aria-label="Clear selection"
    tabIndex={0}
    className="inline-flex items-center justify-center rounded-sm hover:bg-muted-foreground/20 focus:outline-none focus:ring-2 focus:ring-ring h-5 w-5"
    onMouseDown={(e) => {
      e.preventDefault();
      e.stopPropagation();
      clearSelection(e);
    }}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        clearSelection(e as any);
      }
    }}
  >
      <X className="h-3 w-3" />
    </span>
)}
            <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[520px]" align="start">
        <Command shouldFilter={false}>
          <div className="px-2 pt-2">
            <CommandInput
              placeholder="Search…"
              value={q}
              onValueChange={setQ}
              autoFocus
            />
          </div>
          <CommandEmpty>{loading ? "Searching…" : "No users found"}</CommandEmpty>
          <CommandList>
            <CommandGroup>
              {items.map((u) => (
                <CommandItem
                  key={u.id}
                  value={u.id}
                  onSelect={() => handleSelect(u)}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected?.id === u.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {userLabel(u)}
                </CommandItem>
              ))}
              {hasMoreRef.current && (
                <div className="flex items-center justify-center py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => runSearch(false)}
                    disabled={loading}
                    className="text-muted-foreground"
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Load more
                  </Button>
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AsyncUserSelect;
